import React, {
  useState,
  useEffect,
  useRef,
  ReactElement,
  cloneElement,
  ChangeEvent,
} from "react";

interface InputLikeProps {
  value?: string; // Allow optional value prop
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void; // Allow optional onChange
  onInput?: (e: React.ChangeEvent<HTMLInputElement>) => void; // Allow optional onChange
}

interface DebounceWrapperProps {
  debounce?: number;
  onDebouncedChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  children: ReactElement<InputLikeProps>;
  onOriginalInput?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const Debounce: React.FC<DebounceWrapperProps> = ({
  debounce = 500,
  onDebouncedChange,
  children,
  onOriginalInput,
  ...rest
}) => {
  const childValue = children?.props?.value ?? "";
  const [inputValue, setInputValue] = useState<string>(childValue);
  const debouncedValue = useRef<string>(childValue);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update internal state when child's value prop changes
  useEffect(() => {
    setInputValue(childValue);
    debouncedValue.current = childValue;
  }, [childValue]);

  // Handle debounced change logic
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (debouncedValue.current === inputValue) return;

    timeoutRef.current = setTimeout(() => {
      onDebouncedChange?.({
        target: { value: inputValue },
      } as ChangeEvent<HTMLInputElement>);
      debouncedValue.current = inputValue;
    }, debounce);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [inputValue, debounce, onDebouncedChange]);

  // Immediate input change handler
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onOriginalInput?.(e);
    setInputValue(e.target.value); // Update local state immediately
    children.props.onChange?.(e); // Call original onChange, if exists
  };

  // Clone the child component with the new onChange and value props
  const clonedChild = cloneElement(children, {
    ...rest,
    value: inputValue,
    onChange: handleChange,
    onInput: handleChange,
  });

  return clonedChild;
};

export { Debounce };
